import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useColorMode } from '../ColorModeContext';

import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';

function NavButton({ to, label, active }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      color="inherit"
      variant={active ? 'outlined' : 'text'}
      size="small"
      sx={{
        borderColor: active ? 'rgba(255,255,255,0.35)' : 'transparent',
        bgcolor: active ? 'rgba(255,255,255,0.08)' : 'transparent',
        px: 1.25,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Button>
  );
}

export default function NavBar() {
  const { user, logout } = useAuth();
  const { mode, toggle } = useColorMode();
  const location = useLocation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);

  if (!user) return null;

  const isAdmin = user.role === 'admin' || user.role === 'superadmin';
  const isSuperAdmin = user.role === 'superadmin';

  const displayName = `${user.name || user.email}${
    user.role === 'admin'
      ? ' (Admin)'
      : user.role === 'superadmin'
      ? ' (Super Admin)'
      : ''
  }`;

  const showLoanEligibility = isAdmin;
  const showLoanAdmin = isSuperAdmin;

  const path = location.pathname;

  return (
    <AppBar position="sticky" elevation={0} color="primary">
      <Toolbar sx={{ py: 0.75 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {/* Brand */}
            <Typography
              component={RouterLink}
              to="/dashboard"
              variant="h6"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                fontWeight: 900,
                letterSpacing: 0.3,
              }}
            >
              Jetfin
            </Typography>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: 'rgba(255,255,255,0.25)',
                display: { xs: 'none', md: 'block' },
              }}
            />

            {/* Primary nav */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                alignItems: 'center',
                flexWrap: 'wrap',
                flex: 1,
              }}
            >
              <NavButton to="/dashboard" label="Home" active={path.startsWith('/dashboard')} />
              <NavButton
                to="/projects"
                label="Projects"
                active={path.startsWith('/projects') || path.startsWith('/project/')}
              />

              {isAdmin && (
                <>
                  <NavButton
                    to="/layouts"
                    label="Layouts"
                    active={path.startsWith('/layouts') || path.startsWith('/layout-editor')}
                  />
                  <NavButton to="/users" label="Users" active={path.startsWith('/users')} />
                </>
              )}

              {isSuperAdmin && (
                <NavButton
                  to="/recipients"
                  label="Recipients"
                  active={path.startsWith('/recipients')}
                />
              )}

              {showLoanEligibility && (
                <NavButton
                  to="/loan-eligibility"
                  label="Loan Eligibility"
                  active={path.startsWith('/loan-eligibility')}
                />
              )}

              {showLoanAdmin && (
                <NavButton
                  to="/loan-admin"
                  label="Loan Admin"
                  active={path.startsWith('/loan-admin')}
                />
              )}
            </Box>

            {/* Right actions */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}>
                <IconButton color="inherit" onClick={toggle} size="small">
                  {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
                </IconButton>
              </Tooltip>

              <Button
                color="inherit"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={<AccountCircleOutlinedIcon />}
                sx={{ textTransform: 'none' }}
              >
                <Box sx={{ textAlign: 'left', lineHeight: 1.15 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {user.name || 'User'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {user.role}
                  </Typography>
                </Box>
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    navigate('/change-password');
                  }}
                >
                  Change Password
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={async () => {
                    setAnchorEl(null);
                    await logout();
                    navigate('/login');
                  }}
                >
                  Logout
                </MenuItem>
              </Menu>

              <Tooltip title={displayName}>
                <span />
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Toolbar>
    </AppBar>
  );
}
